import * as React from 'react';
import { Button, Typography } from '@/components/common';
import {
	InputCheck,
	InputPhone,
	InputText,
	InputZipCode,
} from '@/components/form';
import { UserI } from '@/interfaces';
import { FieldValues, useForm } from 'react-hook-form';
import { SearchCities } from '@/components/common/search-cities';

interface AccountDetailsProps {
	user?: UserI;
	isConfirm?: boolean;
	isLoading?: boolean;
	isFieldsRequired?: boolean;
	handleConfirmAccountDetails: (data: FieldValues) => void;
}

export const AccountDetails: React.FC<AccountDetailsProps> = ({
	user,
	isConfirm,
	isLoading,
	isFieldsRequired = true,
	handleConfirmAccountDetails,
}) => {
	const {
		register,
		reset,
		handleSubmit,
		setValue,
		formState: { errors, isValid },
		watch,
	} = useForm({
		mode: 'onChange',
	});

	React.useEffect(() => {
		if (user) {
			setTimeout(() => {
				reset({
					firstName: user.firstname,
					lastName: user.lastname,
					address: user.address1,
					state: user.state?.id || null,
					city: user.city?.id || null,
					stateName: user.state?.name || '',
					cityName: user.city?.name || '',
					zipCode: user?.zip_code || null,
					receiveUpdates: user.receive_updates,
					phoneNumber: user.phonenumber,
				});
			}, 1000);
		}
	}, [user]);

	// Rules
	const rules = {
		firstName: {
			required: { value: true, message: 'This is required.' },
		},
		lastName: {
			required: { value: true, message: 'This is required.' },
		},
		address: {
			required: { value: true, message: 'This is required.' },
			minLength: {
				value: 6,
				message: 'Please enter a valid address',
			},
		},
		state: {
			required: { value: true, message: 'This is required.' },
		},
		zipCode: {
			required: { value: true, message: 'This is required.' },
		},
	};

	const submitConfirmAccountDetails = async (
		data: FieldValues
	): Promise<void> => {
		handleConfirmAccountDetails(data);
	};
	return (
		<>
			<Typography
				type="headline-3"
				className="text-black font-bold tracking-tight leading-10 text-center"
			>
				{`${isConfirm ? 'Confirm' : 'Complete'} Your`}{' '}
				<span className="text-primary">Account Details</span>
			</Typography>
			{!isConfirm && (
				<Typography type="regular-18" className="text-black mt-4 text-center">
					{user?.role === 'client'
						? 'Just one more step! Enter the missing account information to continue'
						: 'Just one more step! Enter your account information to continue.'}
				</Typography>
			)}
			<form
				className="mt-8 3xl:mt-12"
				onSubmit={handleSubmit(submitConfirmAccountDetails)}
			>
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
					<InputText
						register={register}
						name="firstName"
						title="First Name"
						customPlaceholder="Ex: Sarah"
						rules={rules.firstName}
						error={errors.firstName}
						markAsRequired={isFieldsRequired}
					/>
					<InputText
						register={register}
						name="lastName"
						title="Last Name"
						customPlaceholder="Ex: Wilson"
						rules={rules.lastName}
						error={errors.lastName}
						className="mb-4"
						markAsRequired={isFieldsRequired}
					/>
				</div>
				{user?.role === 'lawyer' && (
					<InputPhone
						name="phoneNumber"
						title="Phone Number"
						register={register}
						placeholder="Phone number"
						error={errors.phoneNumber}
						setValueInput={setValue}
						className="mb-4"
						markAsRequired={isFieldsRequired}
					/>
				)}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
					<InputText
						register={register}
						name="address"
						title="Street Address"
						customPlaceholder="Ex: 1234 Sunshine Blvd"
						rules={rules.address}
						error={errors.address}
						markAsRequired={isFieldsRequired}
					/>
					<SearchCities
						cityId={user?.city?.id}
						cityName={user?.city?.name}
						stateId={user?.state?.id}
						cityError={errors?.city}
						register={register}
						watch={watch}
						setValue={setValue}
						isFieldsRequired={isFieldsRequired}
					/>
					<InputZipCode
						register={register}
						name="zipCode"
						title="Zip Code"
						customPlaceholder="Ex: 32811"
						rules={rules.zipCode}
						error={errors.zipCode}
						markAsRequired={isFieldsRequired}
						setValueInput={setValue}
					/>
				</div>
				{user?.role === 'client' && (
					<InputCheck
						name="receiveUpdates"
						register={register}
						className="mt-8 3xl:mt-12 !text-left !items-start"
						defaultChecked={true}
					>
						<Typography type="regular-16">
							I wish to receive product news and promotions from Claim Magic by
							email. <br /> I can unsubscribe anytime.
						</Typography>
					</InputCheck>
				)}
				<div className="flex w-full justify-center mt-12">
					<Button
						type="submit"
						label="Continue"
						decoration="primary"
						size="fit"
						disabled={!isValid || !watch('city') || !watch('state')}
						loading={isLoading}
						className="!px-20"
					/>
				</div>
			</form>
		</>
	);
};
